import { useEffect, useRef, useCallback, useState } from 'react';
import { client, DATABASE_ID } from '../lib/appwrite';

/**
 * Subscribes to Appwrite Realtime for table row changes.
 * @param {Object} options
 * @param {string|string[]} options.channels - Channel(s) to subscribe to
 * @param {Function} options.onEvent - Callback when event is received
 * @param {boolean} options.enabled - Whether subscription is enabled (default: true)
 * @param {number} options.debounce - Debounce time in ms (default: 300)
 */
const useRealtimeSubscription = ({ channels, onEvent, enabled = true, debounce = 300 }) => {
  const subscriptionRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  const handleEvent = useCallback((response) => {
    if (!response?.events?.length) return;
    if (debounce > 0) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onEvent({ event: response.events[0], payload: response.payload });
      }, debounce);
    } else {
      onEvent({ event: response.events[0], payload: response.payload });
    }
  }, [onEvent, debounce]);

  useEffect(() => {
    if (!enabled || !channels) return;
    try {
      const channelArray = Array.isArray(channels) ? channels : [channels];
      subscriptionRef.current = client.subscribe(channelArray, handleEvent);
      setIsConnected(true);
    } catch (err) {
      console.warn('Realtime subscription failed:', err);
      setIsConnected(false);
    }
    return () => {
      if (subscriptionRef.current) {
        try { subscriptionRef.current(); } catch (_) {}
        subscriptionRef.current = null;
      }
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      setIsConnected(false);
    };
  }, [channels, enabled, handleEvent]);

  return { isConnected };
};

/** Create channel string for a table's rows: databases.{dbId}.tables.{tableId}.rows */
export const createTableChannel = (tableId) =>
  `databases.${DATABASE_ID}.tables.${tableId}.rows`;

export default useRealtimeSubscription;
